import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import objectFromEntries from 'utils/objectFromEntries';

import PersonGroupTabs from './personGroupTabs';
import PersonMultiSelectTabPanel from './personMultiSelectTabPanel';
import personGroups from './personGroups';

const PersonMultiSelectTabs = ({ isLoading, onSelect, people, personRenderer, treeId }) => {
  const displayedPersonGroups = useMemo(
    () => (treeId ? ['all', 'in-book'] : ['in-book']).map(id => personGroups[id]),
    [treeId],
  );
  const [selectedPeopleByGroup, setSelectedPeopleByGroup] = useState(
    objectFromEntries(displayedPersonGroups.map(({ id }) => [id, []])),
  );
  const [selectedPersonGroup, setSelectedPersonGroup] = useState(displayedPersonGroups[0]);

  const handlePersonGroupChange = useCallback(
    newSelectedPersonGroup => {
      const { id } = newSelectedPersonGroup;
      setSelectedPersonGroup(newSelectedPersonGroup);
      onSelect(selectedPeopleByGroup[id]);
    },
    [onSelect, selectedPeopleByGroup],
  );

  const handleRemove = useCallback(
    ({ person }) => {
      const { id } = selectedPersonGroup;
      setSelectedPeopleByGroup(currentSelectedPeopleByGroup => {
        const newSelectedPeople = currentSelectedPeopleByGroup[id].filter(
          selectedPerson => selectedPerson.id !== person.id,
        );
        onSelect(newSelectedPeople);
        return {
          ...currentSelectedPeopleByGroup,
          [id]: newSelectedPeople,
        };
      });
    },
    [onSelect, selectedPersonGroup],
  );

  const handleSelect = useCallback(
    ({ person }) => {
      const { id } = selectedPersonGroup;
      setSelectedPeopleByGroup(currentSelectedPeopleByGroup => {
        const newSelectedPeople = [...currentSelectedPeopleByGroup[id], person];
        onSelect(newSelectedPeople);
        return {
          ...currentSelectedPeopleByGroup,
          [id]: newSelectedPeople,
        };
      });
    },
    [onSelect, selectedPersonGroup],
  );

  return (
    <PersonGroupTabs
      isLoading={isLoading}
      onChange={handlePersonGroupChange}
      panel={
        <PersonMultiSelectTabPanel
          onRemove={handleRemove}
          onSelect={handleSelect}
          selectedPeopleByGroup={selectedPeopleByGroup}
          personRenderer={personRenderer}
        />
      }
      people={people}
      personGroups={displayedPersonGroups}
      selectedPersonGroup={selectedPersonGroup}
      treeId={treeId}
      includeCitations={true}
    />
  );
};

PersonMultiSelectTabs.defaultProps = {
  isLoading: false,
  people: [],
};

PersonMultiSelectTabs.propTypes = {
  isLoading: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  people: PropTypes.arrayOf(PropTypes.object),
  treeId: PropTypes.string,
  personRenderer: PropTypes.func,
};

export default PersonMultiSelectTabs;
